import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAgentAuth } from '../../../contexts/AgentAuthContext';
import { useAgentToolUsage } from '../../../hooks/useAgentToolUsage';
import { TOOL_IDS } from '../../../lib/agents/firestore-schema';
import {
  saveFnaFormData,
  saveFnaReport,
  getFnaFormData,
  listFnaReports,
  listFnaFormData,
} from '../../../lib/agents/fnaStorage';
import FNAReportView from './FNAReportView';
import {
  calculateSurvivorNeeds,
  calculateDisabilityNeeds,
  calculateLTCNeeds,
  calculateEmergencyFund,
  calculateDebtManagement,
  calculateRetirementNeeds,
  calculateEducationNeeds,
  calculateCashFlow,
} from '../../../lib/agents/fnaCalculations';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useToast } from '../../../hooks/use-toast';
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Download, Mail, FileText, CheckCircle2, Save, Loader2, AlertCircle, FolderOpen, X, Users } from 'lucide-react';
import './fna-portal.css';

const STEPS = [
  { id: 'personal', label: 'Personal Info' },
  { id: 'income', label: 'Income & Assets' },
  { id: 'debts', label: 'Debts' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'insurance', label: 'Existing Insurance' },
  { id: 'goals', label: 'Goals' },
  { id: 'assumptions', label: 'Assumptions' },
  { id: 'report', label: 'Report' },
];

const DEFAULT_EXPENSE_CATEGORIES = [
  'Food and Clothing',
  'Auto and Property Maint./Ins./Taxes',
  'Utilities',
  'Personal Expenses',
  'Gifts, Charity',
  'Entertainment',
  'HOA, Kids Education',
];

export default function FinancialNeedAnalysis() {
  const navigate = useNavigate();
  const { reportId: editReportId } = useParams();
  const { agentProfile } = useAgentAuth();
  const usage = useAgentToolUsage(agentProfile, TOOL_IDS.FINANCIAL_NEED_ANALYSIS);
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [report, setReport] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showReportSelector, setShowReportSelector] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState('');
  const [selectedReportId, setSelectedReportId] = useState('');
  const [currentClientDocId, setCurrentClientDocId] = useState(null);

  // Personal Information
  const [client1, setClient1] = useState({ name: '', dob: '', gender: 'Male' });
  const [client2, setClient2] = useState({ name: '', dob: '', gender: 'Female' });
  const [children, setChildren] = useState([]);
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');

  // Income & Assets
  const [client1Salary, setClient1Salary] = useState('');
  const [client2Salary, setClient2Salary] = useState('');
  const [taxRate, setTaxRate] = useState('20');
  const [assets, setAssets] = useState([]);
  const [retirementAssets, setRetirementAssets] = useState([]);
  const [educationSavings, setEducationSavings] = useState('');

  // Debts
  const [debts, setDebts] = useState([]);

  // Expenses (monthly lifestyle / expense categories)
  const [expenses, setExpenses] = useState(() =>
    DEFAULT_EXPENSE_CATEGORIES.map((category) => ({ category, amount: '' }))
  );

  // Existing Insurance
  const [lifeInsurance, setLifeInsurance] = useState([]);
  const [disabilityInsurance, setDisabilityInsurance] = useState([]);
  const [ltcInsurance, setLtcInsurance] = useState([]);

  // Goals
  const [shortTermGoals, setShortTermGoals] = useState([]);
  const [midTermGoals, setMidTermGoals] = useState([]);
  const [longTermGoals, setLongTermGoals] = useState([]);
  const [retirementAge, setRetirementAge] = useState('65');
  const [retirementIncomePercent, setRetirementIncomePercent] = useState('80');

  // Assumptions (used for report generation; user can override defaults)
  const [assumptionTaxRate, setAssumptionTaxRate] = useState('20');
  const [assumptionInflationRate, setAssumptionInflationRate] = useState('3');
  const [assumptionEducationInflation, setAssumptionEducationInflation] = useState('6');
  const [assumptionReturnRate, setAssumptionReturnRate] = useState('5');
  const [assumptionRetirementGrowth, setAssumptionRetirementGrowth] = useState('8');
  const [assumptionSurvivorReturn, setAssumptionSurvivorReturn] = useState('5');
  const [assumptionLTCReturn, setAssumptionLTCReturn] = useState('4');
  const [assumptionSurvivorIncomePctWithKids, setAssumptionSurvivorIncomePctWithKids] = useState('70');
  const [assumptionSurvivorIncomePctAfterKids, setAssumptionSurvivorIncomePctAfterKids] = useState('60');
  const [assumptionFinalExpenses, setAssumptionFinalExpenses] = useState('10000');
  const [assumptionLifeExpectancy, setAssumptionLifeExpectancy] = useState('81');
  const [assumptionEmergencyMonths, setAssumptionEmergencyMonths] = useState('3');
  const [assumptionEducationCost, setAssumptionEducationCost] = useState('50000');
  const [assumptionEducationYears, setAssumptionEducationYears] = useState('4');
  const [assumptionEducationPercentFund, setAssumptionEducationPercentFund] = useState('100');
  const [assumptionRetirementYears, setAssumptionRetirementYears] = useState('20');
  const [assumptionSocialSecurity, setAssumptionSocialSecurity] = useState('0');
  const [assumptionDisabilityPct, setAssumptionDisabilityPct] = useState('60');
  const [assumptionLTCAge, setAssumptionLTCAge] = useState('75');
  const [assumptionLTCCost, setAssumptionLTCCost] = useState('6000');
  const [assumptionLTCDuration, setAssumptionLTCDuration] = useState('3');
  const [assumptionLTCInflation, setAssumptionLTCInflation] = useState('3');
  const [assumptionCollapsed, setAssumptionCollapsed] = useState({ tax: false, inflation: true, investment: true, survivor: true, emergency: true, education: true, retirement: true, disability: true, ltc: true });

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const autoSaveTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Collect all form data into a draft object
  const getDraftData = useCallback(() => {
    return {
      currentStep,
      client1,
      client2,
      children,
      address,
      email,
      client1Salary,
      client2Salary,
      taxRate,
      assets,
      retirementAssets,
      educationSavings,
      debts,
      expenses,
      lifeInsurance,
      disabilityInsurance,
      ltcInsurance,
      shortTermGoals,
      midTermGoals,
      longTermGoals,
      retirementAge,
      retirementIncomePercent,
      assumptionTaxRate,
      assumptionInflationRate,
      assumptionEducationInflation,
      assumptionReturnRate,
      assumptionRetirementGrowth,
      assumptionSurvivorReturn,
      assumptionLTCReturn,
      assumptionSurvivorIncomePctWithKids,
      assumptionSurvivorIncomePctAfterKids,
      assumptionFinalExpenses,
      assumptionLifeExpectancy,
      assumptionEmergencyMonths,
      assumptionEducationCost,
      assumptionEducationYears,
      assumptionEducationPercentFund,
      assumptionRetirementYears,
      assumptionSocialSecurity,
      assumptionDisabilityPct,
      assumptionLTCAge,
      assumptionLTCCost,
      assumptionLTCDuration,
      assumptionLTCInflation,
    };
  }, [
    currentStep,
    client1,
    client2,
    children,
    address,
    email,
    client1Salary,
    client2Salary,
    taxRate,
    assets,
    retirementAssets,
    educationSavings,
    debts,
    expenses,
    lifeInsurance,
    disabilityInsurance,
    ltcInsurance,
    shortTermGoals,
    midTermGoals,
    longTermGoals,
    retirementAge,
    retirementIncomePercent,
    assumptionTaxRate,
    assumptionInflationRate,
    assumptionEducationInflation,
    assumptionReturnRate,
    assumptionRetirementGrowth,
    assumptionSurvivorReturn,
    assumptionLTCReturn,
    assumptionSurvivorIncomePctWithKids,
    assumptionSurvivorIncomePctAfterKids,
    assumptionFinalExpenses,
    assumptionLifeExpectancy,
    assumptionEmergencyMonths,
    assumptionEducationCost,
    assumptionEducationYears,
    assumptionEducationPercentFund,
    assumptionRetirementYears,
    assumptionSocialSecurity,
    assumptionDisabilityPct,
    assumptionLTCAge,
    assumptionLTCCost,
    assumptionLTCDuration,
    assumptionLTCInflation,
  ]);

  // Auto-save function with debouncing (requires client email for FNA schema)
  const performAutoSave = useCallback(async () => {
    if (!agentProfile?.uid || !agentProfile?.email) return;
    if (isInitialLoadRef.current) return;
    const clientEmail = (email || '').trim().toLowerCase();
    if (!clientEmail || !clientEmail.includes('@')) return;

    setAutoSaveStatus('saving');
    try {
      await saveFnaFormData({
        userId: agentProfile.uid,
        userEmail: agentProfile.email,
        clientEmail,
        formData: getDraftData(),
      });
      setAutoSaveStatus('saved');
      // Reset status after 2 seconds
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
      // Show error toast for debugging
      toast({
        title: 'Auto-save failed',
        description: error.message || 'Failed to save draft. Check console for details.',
        variant: 'destructive',
      });
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 5000); // Show error longer
    }
  }, [agentProfile, getDraftData, email]);

  // Debounced auto-save effect
  useEffect(() => {
    if (isInitialLoadRef.current) return;

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (1.5 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, 1500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [performAutoSave, getDraftData]);

  // Mark initial load complete after auth ready (no auto-load draft - each client has own form)
  useEffect(() => {
    if (!agentProfile?.uid) {
      const timer = setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 1000);
      return () => clearTimeout(timer);
    }
    isInitialLoadRef.current = false;
  }, [agentProfile?.uid]);

  // Load saved reports and form data (from FNA collections)
  useEffect(() => {
    const loadSavedData = async () => {
      if (!agentProfile?.uid) return;
      
      setLoadingReports(true);
      try {
        // Load saved reports
        const reports = await listFnaReports({ userId: agentProfile.uid, limitVal: 100 });
        setSavedReports(reports);

        const formDataList = await listFnaFormData({ userId: agentProfile.uid });
        setSavedDrafts(formDataList);
      } catch (error) {
        console.error('Failed to load saved data:', error);
      } finally {
        setLoadingReports(false);
      }
    };

    loadSavedData();
  }, [agentProfile?.uid, agentProfile?.email]);

  // Load report data into form
  const loadReportData = useCallback((reportData) => {
    if (!reportData) return;

    // Restore all form fields from report data
    // Note: reportData structure matches what we save in generateReport
    if (reportData.client1) {
      setClient1({
        name: reportData.client1.name || '',
        dob: reportData.client1.dob || '',
        gender: reportData.client1.gender || 'Male',
      });
      // Salary is stored separately in form state
      if (reportData.client1.salary !== undefined) {
        setClient1Salary(String(reportData.client1.salary));
      }
    }
    if (reportData.client2) {
      setClient2({
        name: reportData.client2.name || '',
        dob: reportData.client2.dob || '',
        gender: reportData.client2.gender || 'Female',
      });
      if (reportData.client2.salary !== undefined) {
        setClient2Salary(String(reportData.client2.salary));
      }
    }
    if (reportData.children) setChildren(reportData.children || []);
    if (reportData.address !== undefined) setAddress(reportData.address || '');
    if (reportData.email !== undefined) setEmail(reportData.email || '');
    
    // Note: taxRate, assets, retirementAssets, educationSavings, debts, insurance, goals
    // are not stored in reportData - they're only in draft. 
    // But we can try to restore from draft if available, or leave empty for user to fill
    
    // Reset to first step
    setCurrentStep(0);
    setReport(null);
    setReportId(null);
    setShowReportSelector(false);
    setSelectedDraftId('');
    setSelectedReportId('');

    toast({
      title: 'Client data loaded',
      description: `Loaded data for ${reportData.client1?.name || reportData.client2?.name || 'client'}. Please review and complete remaining fields.`,
    });
  }, [toast]);

  const handleSelectReport = useCallback((selectedReport) => {
    if (selectedReport?.reportData) {
      loadReportData(selectedReport.reportData);
    }
  }, [loadReportData]);

  // Load draft data into form (includes all fields)
  const loadDraftData = useCallback((draftData) => {
    if (!draftData || typeof draftData !== 'object') return;

    // Normalize client objects (handle partial/legacy data)
    const safeClient = (c, defaults = { name: '', dob: '', gender: 'Male' }) =>
      c && typeof c === 'object' ? { ...defaults, ...c } : defaults;

    // Restore all form fields from draft data
    if (draftData.currentStep !== undefined) setCurrentStep(draftData.currentStep);
    if (draftData.client1) setClient1(safeClient(draftData.client1, { name: '', dob: '', gender: 'Male' }));
    if (draftData.client2) setClient2(safeClient(draftData.client2, { name: '', dob: '', gender: 'Female' }));
    if (Array.isArray(draftData.children)) setChildren(draftData.children);
    if (draftData.address !== undefined) setAddress(draftData.address);
    if (draftData.email !== undefined) setEmail(draftData.email);
    if (draftData.client1Salary !== undefined) setClient1Salary(draftData.client1Salary);
    if (draftData.client2Salary !== undefined) setClient2Salary(draftData.client2Salary);
    if (draftData.taxRate !== undefined) setTaxRate(draftData.taxRate);
    if (Array.isArray(draftData.assets)) setAssets(draftData.assets);
    if (Array.isArray(draftData.retirementAssets)) setRetirementAssets(draftData.retirementAssets);
    if (draftData.educationSavings !== undefined) setEducationSavings(draftData.educationSavings);
    if (Array.isArray(draftData.debts)) setDebts(draftData.debts);
    if (Array.isArray(draftData.expenses)) setExpenses(draftData.expenses);
    if (Array.isArray(draftData.lifeInsurance)) setLifeInsurance(draftData.lifeInsurance);
    if (Array.isArray(draftData.disabilityInsurance)) setDisabilityInsurance(draftData.disabilityInsurance);
    if (Array.isArray(draftData.ltcInsurance)) setLtcInsurance(draftData.ltcInsurance);
    if (Array.isArray(draftData.shortTermGoals)) setShortTermGoals(draftData.shortTermGoals);
    if (Array.isArray(draftData.midTermGoals)) setMidTermGoals(draftData.midTermGoals);
    if (Array.isArray(draftData.longTermGoals)) setLongTermGoals(draftData.longTermGoals);
    if (draftData.retirementAge !== undefined) setRetirementAge(draftData.retirementAge);
    if (draftData.retirementIncomePercent !== undefined) setRetirementIncomePercent(draftData.retirementIncomePercent);
    if (draftData.assumptionTaxRate !== undefined) setAssumptionTaxRate(draftData.assumptionTaxRate);
    if (draftData.assumptionInflationRate !== undefined) setAssumptionInflationRate(draftData.assumptionInflationRate);
    if (draftData.assumptionEducationInflation !== undefined) setAssumptionEducationInflation(draftData.assumptionEducationInflation);
    if (draftData.assumptionReturnRate !== undefined) setAssumptionReturnRate(draftData.assumptionReturnRate);
    if (draftData.assumptionRetirementGrowth !== undefined) setAssumptionRetirementGrowth(draftData.assumptionRetirementGrowth);
    if (draftData.assumptionSurvivorReturn !== undefined) setAssumptionSurvivorReturn(draftData.assumptionSurvivorReturn);
    if (draftData.assumptionLTCReturn !== undefined) setAssumptionLTCReturn(draftData.assumptionLTCReturn);
    if (draftData.assumptionSurvivorIncomePctWithKids !== undefined) setAssumptionSurvivorIncomePctWithKids(draftData.assumptionSurvivorIncomePctWithKids);
    if (draftData.assumptionSurvivorIncomePctAfterKids !== undefined) setAssumptionSurvivorIncomePctAfterKids(draftData.assumptionSurvivorIncomePctAfterKids);
    if (draftData.assumptionFinalExpenses !== undefined) setAssumptionFinalExpenses(draftData.assumptionFinalExpenses);
    if (draftData.assumptionLifeExpectancy !== undefined) setAssumptionLifeExpectancy(draftData.assumptionLifeExpectancy);
    if (draftData.assumptionEmergencyMonths !== undefined) setAssumptionEmergencyMonths(draftData.assumptionEmergencyMonths);
    if (draftData.assumptionEducationCost !== undefined) setAssumptionEducationCost(draftData.assumptionEducationCost);
    if (draftData.assumptionEducationYears !== undefined) setAssumptionEducationYears(draftData.assumptionEducationYears);
    if (draftData.assumptionEducationPercentFund !== undefined) setAssumptionEducationPercentFund(draftData.assumptionEducationPercentFund);
    if (draftData.assumptionRetirementYears !== undefined) setAssumptionRetirementYears(draftData.assumptionRetirementYears);
    if (draftData.assumptionSocialSecurity !== undefined) setAssumptionSocialSecurity(draftData.assumptionSocialSecurity);
    if (draftData.assumptionDisabilityPct !== undefined) setAssumptionDisabilityPct(draftData.assumptionDisabilityPct);
    if (draftData.assumptionLTCAge !== undefined) setAssumptionLTCAge(draftData.assumptionLTCAge);
    if (draftData.assumptionLTCCost !== undefined) setAssumptionLTCCost(draftData.assumptionLTCCost);
    if (draftData.assumptionLTCDuration !== undefined) setAssumptionLTCDuration(draftData.assumptionLTCDuration);
    if (draftData.assumptionLTCInflation !== undefined) setAssumptionLTCInflation(draftData.assumptionLTCInflation);

    setReport(null);
    setReportId(null);
    setShowReportSelector(false);
    setSelectedReportId('');

    toast({
      title: 'Draft loaded',
      description: `Loaded draft for ${draftData.client1?.name || draftData.client2?.name || 'client'}`,
    });
  }, [toast]);

  // Load client data when opening in edit mode (from dashboard Edit & Regenerate)
  // Use listFnaReports (same query as dashboard) then find by id to avoid permission issues with direct getDoc.
  useEffect(() => {
    if (!editReportId || !agentProfile?.uid) return;

    const loadForEdit = async () => {
      try {
        // Fetch via list query (allowed by rules) instead of getDoc to avoid "missing or insufficient permissions"
        const list = await listFnaReports({ userId: agentProfile.uid, limitVal: 200 });
        const report = list.find((r) => r.id === editReportId);
        if (!report) {
          toast({ title: 'Report not found', description: 'This client report could not be loaded.', variant: 'destructive' });
          navigate('/agents/financial-need-analysis');
          return;
        }

        // Prefer form data (has all fields); fallback to report data. Skip getFnaFormData if it would fail.
        const clientEmail = report.clientEmail || report.reportData?.email;
        if (clientEmail) {
          try {
            const formData = await getFnaFormData({ userId: agentProfile.uid, clientEmail });
            if (formData && typeof formData === 'object') {
              loadDraftData(formData);
              setCurrentClientDocId(editReportId);
              setReportId(editReportId);
              return;
            }
          } catch (_) {
            // Form data read may be denied; fall back to report data
          }
        }

        if (report.reportData) {
          loadReportData(report.reportData);
        }
        setCurrentClientDocId(editReportId);
        setReportId(editReportId);
      } catch (err) {
        console.error('Failed to load report for edit:', err);
        toast({ title: 'Load failed', description: err.message || 'Could not load client data.', variant: 'destructive' });
        navigate('/agents/financial-need-analysis');
      }
    };

    loadForEdit();
  }, [editReportId, agentProfile?.uid, loadDraftData, loadReportData, toast, navigate]);

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const generateReport = async () => {
    const age1 = calculateAge(client1.dob);
    const age2 = calculateAge(client2.dob);
    const salary1 = Number(client1Salary) || 0;
    const salary2 = Number(client2Salary) || 0;
    const householdIncome = salary1 + salary2;
    const monthlySalary = householdIncome / 12;
    const tax = Number(assumptionTaxRate || taxRate) / 100;
    const inflationRate = Number(assumptionInflationRate || 3) / 100;
    const educationInflation = Number(assumptionEducationInflation || 6) / 100;
    const returnRate = Number(assumptionReturnRate || 5) / 100;
    const survivorReturn = Number(assumptionSurvivorReturn || 5) / 100;
    const ltcReturn = Number(assumptionLTCReturn || 4) / 100;
    const retirementGrowth = Number(assumptionRetirementGrowth || 8) / 100;
    const survivorPctWithKids = Number(assumptionSurvivorIncomePctWithKids || 70) / 100;
    const survivorPctAfterKids = Number(assumptionSurvivorIncomePctAfterKids || 60) / 100;
    const finalExpenses = Number(assumptionFinalExpenses || 10000);
    const lifeExpectancy = Number(assumptionLifeExpectancy || 81);
    const emergencyMonths = Number(assumptionEmergencyMonths || 3);
    const educationCostToday = Number(assumptionEducationCost || 50000);
    const educationYears = Number(assumptionEducationYears || 4);
    const educationPercentFund = Number(assumptionEducationPercentFund || 100) / 100;
    const retirementYears = Number(assumptionRetirementYears || 20);
    const socialSecurity = Number(assumptionSocialSecurity || 0);
    const disabilityPct = Number(assumptionDisabilityPct || 60) / 100;
    const ltcAge = Number(assumptionLTCAge || 75);
    const ltcCost = Number(assumptionLTCCost || 6000);
    const ltcDuration = Number(assumptionLTCDuration || 3);
    const ltcInflation = Number(assumptionLTCInflation || 3) / 100;

    // Calculate total assets available
    const totalAssets = assets.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const totalRetirementAssets = retirementAssets.reduce((sum, r) => sum + (Number(r.currentAmount) || 0), 0);

    // Calculate total debts
    const totalDebts = debts.reduce((sum, d) => sum + (Number(d.balance) || 0), 0);
    const mortgageDebt = debts.find(d => d.description?.toLowerCase().includes('house') || d.description?.toLowerCase().includes('mortgage'));
    const mortgageBalance = mortgageDebt ? Number(mortgageDebt.balance) || 0 : 0;
    const otherDebts = totalDebts - mortgageBalance;

    // Calculate monthly debt payments
    const monthlyDebtPayments = debts.reduce((sum, d) => sum + (Number(d.monthlyPayment) || 0), 0);

    // Calculate cash flow FIRST so we can use totalExpenses for the emergency fund basis
    const totalLifestyleExpenses = (expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const cashFlowCalc = calculateCashFlow({
      monthlySalary,
      taxRate: tax,
      monthlyDebtPayments,
      monthlySavings: retirementAssets.reduce((sum, r) => sum + (Number(r.monthlySavings) || 0), 0),
      lifeInsurancePremiums: lifeInsurance.reduce((sum, p) => sum + (Number(p.annualPremium) || 0) / 12, 0),
      lifestyleExpenses: totalLifestyleExpenses,
    });

    // Emergency fund: target = monthsCoverage × monthly EXPENSES (not gross salary)
    // This is the correct financial planning standard — how long can you cover what you spend?
    const currentEmergencyFund = assets.find(a => a.availableForEmergency)
      ? Number(assets.find(a => a.availableForEmergency).amount) || 0
      : 0;
    const emergencyFundCalc = calculateEmergencyFund({
      monthlySalary,
      monthlyExpenses: cashFlowCalc.totalExpenses > 0 ? cashFlowCalc.totalExpenses : cashFlowCalc.netIncome,
      monthsCoverage: emergencyMonths,
      currentEmergencyFund,
    });

    // Calculate education needs
    const educationCalc = calculateEducationNeeds({
      children: children.map(c => ({
        name: c.name,
        currentAge: calculateAge(c.dob),
        startAge: 18,
        annualCostToday: educationCostToday,
        percentToFund: educationPercentFund,
        years: educationYears,
      })),
      currentEducationSavings: Number(educationSavings) || 0,
      educationInflationRate: educationInflation,
      returnRate,
    });

    // Calculate retirement needs
    const retirementCalc = calculateRetirementNeeds({
      currentAge: Math.min(age1, age2),
      retirementAge: Number(retirementAge) || 65,
      householdIncome,
      retirementIncomePercent: Number(retirementIncomePercent) / 100,
      retirementYears,
      currentRetirementAssets: totalRetirementAssets,
      monthlyRetirementContributions: retirementAssets.reduce((sum, r) => sum + (Number(r.monthlySavings) || 0), 0),
      retirementGrowthRate: retirementGrowth,
      socialSecurityBenefit: socialSecurity,
      inflationRate,
      returnRate,
    });

    // Calculate survivor needs for client 1
    const youngestChildAge = children.length > 0 
      ? Math.min(...children.map(c => calculateAge(c.dob)))
      : 0;
    const yearsUntilYoungest18 = Math.max(0, 18 - youngestChildAge);
    
    const survivor1Calc = calculateSurvivorNeeds({
      annualIncome: salary1,
      spouseIncome: salary2,
      totalDebts: otherDebts,
      mortgageBalance,
      educationCost: educationCalc.totalLumpSumNeeded,
      emergencyFundNeed: emergencyFundCalc.totalNeed,
      finalExpenses,
      yearsUntilYoungest18,
      survivorAge: age2,
      lifeExpectancy,
      existingInsurance: lifeInsurance.filter(p => p.insured === client1.name).reduce((sum, p) => sum + (Number(p.deathBenefit) || 0), 0),
      availableAssets: totalAssets + totalRetirementAssets,
      inflationRate,
      returnRate: survivorReturn,
      incomeReplacementPctWithChildren: survivorPctWithKids,
      incomeReplacementPctAfterChildren: survivorPctAfterKids,
    });

    const survivor2Calc = calculateSurvivorNeeds({
      annualIncome: salary2,
      spouseIncome: salary1,
      totalDebts: otherDebts,
      mortgageBalance,
      educationCost: educationCalc.totalLumpSumNeeded,
      emergencyFundNeed: emergencyFundCalc.totalNeed,
      finalExpenses,
      yearsUntilYoungest18,
      survivorAge: age1,
      lifeExpectancy,
      existingInsurance: lifeInsurance.filter(p => p.insured === client2.name).reduce((sum, p) => sum + (Number(p.deathBenefit) || 0), 0),
      availableAssets: totalAssets + totalRetirementAssets,
      inflationRate,
      returnRate: survivorReturn,
      incomeReplacementPctWithChildren: survivorPctWithKids,
      incomeReplacementPctAfterChildren: survivorPctAfterKids,
    });

    // Calculate disability needs
    const disability1Calc = calculateDisabilityNeeds({
      annualSalary: salary1,
      percentNeeded: disabilityPct,
      existingDisabilityIncome: disabilityInsurance.filter(p => p.insured === client1.name).reduce((sum, p) => sum + (Number(p.monthlyBenefit) || 0) * 12, 0),
    });

    const disability2Calc = calculateDisabilityNeeds({
      annualSalary: salary2,
      percentNeeded: disabilityPct,
      existingDisabilityIncome: disabilityInsurance.filter(p => p.insured === client2.name).reduce((sum, p) => sum + (Number(p.monthlyBenefit) || 0) * 12, 0),
    });

    // Calculate LTC needs
    const ltc1Calc = calculateLTCNeeds({
      currentAge: age1,
      ltcAge,
      monthlyNeedToday: ltcCost,
      inflationRate: ltcInflation,
      durationYears: ltcDuration,
      returnRate: ltcReturn,
      existingLTCBenefit: ltcInsurance.filter(p => p.insured === client1.name).reduce((sum, p) => sum + (Number(p.monthlyBenefit) || 0), 0),
    });

    const ltc2Calc = calculateLTCNeeds({
      currentAge: age2,
      ltcAge,
      monthlyNeedToday: ltcCost,
      inflationRate: ltcInflation,
      durationYears: ltcDuration,
      returnRate: ltcReturn,
      existingLTCBenefit: ltcInsurance.filter(p => p.insured === client2.name).reduce((sum, p) => sum + (Number(p.monthlyBenefit) || 0), 0),
    });

    // Calculate debt management
    const debtCalc = calculateDebtManagement({
      debts,
      monthlyIncome: monthlySalary,
    });

    // cashFlowCalc is computed above (before emergencyFundCalc) so it is available here

    const reportData = {
      client1: { ...client1, age: age1, salary: salary1 },
      client2: { ...client2, age: age2, salary: salary2 },
      children: children.map(c => ({ ...c, currentAge: calculateAge(c.dob) })),
      address,
      email,
      assets: assets || [],
      debts: debts || [],
      expenses: expenses || [],
      lifeInsurance: lifeInsurance || [],
      generatedAt: new Date().toISOString(),
      calculations: {
        cashFlow: cashFlowCalc,
        survivor1: survivor1Calc,
        survivor2: survivor2Calc,
        disability1: disability1Calc,
        disability2: disability2Calc,
        ltc1: ltc1Calc,
        ltc2: ltc2Calc,
        emergencyFund: emergencyFundCalc,
        debtManagement: debtCalc,
        retirement: retirementCalc,
        education: educationCalc,
      },
      goals: {
        shortTerm: shortTermGoals,
        midTerm: midTermGoals,
        longTerm: longTermGoals,
      },
      assumptions: {
        taxRate: Number(assumptionTaxRate || 20),
        inflationRate: Number(assumptionInflationRate || 3),
        educationInflation: Number(assumptionEducationInflation || 6),
        returnRate: Number(assumptionReturnRate || 5),
        survivorReturn: Number(assumptionSurvivorReturn || 5),
        retirementGrowth: Number(assumptionRetirementGrowth || 8),
        ltcReturn: Number(assumptionLTCReturn || 4),
        survivorIncomePctWithKids: Number(assumptionSurvivorIncomePctWithKids || 70),
        survivorIncomePctAfterKids: Number(assumptionSurvivorIncomePctAfterKids || 60),
        finalExpenses: Number(assumptionFinalExpenses || 10000),
        lifeExpectancy: Number(assumptionLifeExpectancy || 81),
        emergencyMonths: Number(assumptionEmergencyMonths || 3),
        educationCost: Number(assumptionEducationCost || 50000),
        educationYears: Number(assumptionEducationYears || 4),
        educationPercentFund: Number(assumptionEducationPercentFund || 100),
        retirementYears: Number(assumptionRetirementYears || 20),
        socialSecurity: Number(assumptionSocialSecurity || 0),
        disabilityPct: Number(assumptionDisabilityPct || 60),
        ltcAge: Number(assumptionLTCAge || 75),
        ltcCost: Number(assumptionLTCCost || 6000),
        ltcDuration: Number(assumptionLTCDuration || 3),
        ltcInflation: Number(assumptionLTCInflation || 3),
      },
    };

    setReport(reportData);
    setCurrentStep(STEPS.length - 1); // Advance to Report step

    // Save to FNA collection (requires valid client email for dashboard)
    const clientEmail = ((email || reportData.email) || '').toString().trim().toLowerCase();
    const hasValidEmail = clientEmail && clientEmail.includes('@');

    try {
      if (agentProfile?.uid && agentProfile?.email && hasValidEmail) {
        const docId = await saveFnaReport({
          userId: agentProfile.uid,
          userEmail: agentProfile.email,
          clientEmail,
          reportData,
          clientSnapshot: { client1, client2, address, children },
        });
        setReportId(docId);
        setCurrentClientDocId(docId);
        usage.recordReport({
          clientName: client1.name || client2.name || 'Client',
          reportId: docId,
        });
        toast({ title: 'Report generated & saved', description: 'Report is saved. View it anytime from the FNA Client Dashboard.' });
      } else if (!hasValidEmail) {
        toast({
          title: 'Report not saved to dashboard',
          description: 'Add client email in Personal Info (Step 1) and regenerate to save to your dashboard.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Report generated', description: 'Sign in to save reports to your dashboard.' });
      }
    } catch (error) {
      console.error('Failed to save report to database:', error);
      toast({
        title: 'Report saved locally only',
        description: error.message || 'Could not save to database. Check Firestore rules and connection.',
        variant: 'destructive',
      });
    }
    
    usage.recordStep('report_generated', { comprehensive: true });
    setCurrentStep(STEPS.length - 1);
  };

  const handleShareByEmail = () => {
    if (!report) return;
    const subject = `Financial Need Analysis - ${report.client1.name || 'Report'}`;
    const body = `Please find attached the Financial Need Analysis report.\n\nGenerated: ${new Date(report.generatedAt).toLocaleDateString()}`;
    const mailto = `mailto:${report.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    usage.recordShare({ method: 'email', clientName: report.client1.name });
    window.open(mailto);
    toast({ title: 'Share by email', description: 'Email client opened.' });
  };

  const handleDownloadPdf = async (reportElement) => {
    if (!report || !reportElement) {
      toast({ title: 'Error', description: 'Report element not found. Please try again.', variant: 'destructive' });
      return;
    }

    try {
      toast({ title: 'Generating PDF', description: 'Building your report — this takes about 10 seconds…' });

      // Small delay so the toast renders before the heavy canvas work starts
      await new Promise((r) => setTimeout(r, 400));

      const html2pdf = (await import('html2pdf.js')).default;

      // Measure the full content dimensions BEFORE any DOM manipulation
      const fullWidth = reportElement.scrollWidth || reportElement.offsetWidth || 794;
      const fullHeight = reportElement.scrollHeight || reportElement.offsetHeight;

      const opt = {
        margin: [8, 8],
        filename: `GrowAndStayRich-FNA-${(report.client1?.name || 'client').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          letterRendering: true,
          logging: false,
          backgroundColor: '#ffffff',
          // Tell html2canvas the full document dimensions so it captures all content,
          // not just what is currently visible in the viewport
          windowWidth: fullWidth,
          windowHeight: fullHeight,
          scrollX: 0,
          scrollY: 0,
          // onclone: strip every overflow/clip/maxHeight constraint in the cloned DOM
          // so html2canvas renders 100% of the content, even inside scroll containers
          onclone: (clonedDoc) => {
            clonedDoc.querySelectorAll('*').forEach((el) => {
              const s = el.style;
              if (!s) return;
              if (s.overflow && s.overflow !== 'visible') s.overflow = 'visible';
              if (s.overflowY && s.overflowY !== 'visible') s.overflowY = 'visible';
              if (s.overflowX && s.overflowX !== 'visible') s.overflowX = 'visible';
              if (s.maxHeight) s.maxHeight = 'none';
              if (s.height && (s.height.includes('calc') || s.height.includes('vh'))) s.height = 'auto';
              // Also strip Tailwind scroll/clip classes — use baseVal for SVGAnimatedString
              const c = typeof el.className === 'string' ? el.className : (el.className?.baseVal ?? '');
              if (c.includes('overflow') || c.includes('max-h-')) {
                s.overflow = 'visible';
                s.maxHeight = 'none';
                s.height = 'auto';
              }
            });
          },
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak: { mode: ['css', 'legacy'] },
      };

      let pdfBlob = null;
      if (agentProfile?.uid && agentProfile?.email) {
        try {
          pdfBlob = await html2pdf().set(opt).from(reportElement).outputPdf('blob');
        } catch (blobError) {
          console.warn('Blob generation failed, continuing with save:', blobError);
        }
      }

      await html2pdf().set(opt).from(reportElement).save();

      if (pdfBlob && agentProfile?.uid && agentProfile?.email) {
        try {
          const { savePdfReport } = await import('../../../lib/agents/premiumReportGenerator');
          await savePdfReport({
            userId: agentProfile.uid,
            userEmail: agentProfile.email,
            toolId: TOOL_IDS.FINANCIAL_NEED_ANALYSIS,
            reportData: report,
            pdfBlob,
          });
        } catch (saveError) {
          console.error('Failed to save PDF to database:', saveError);
        }
      }

      usage.recordPdfExport({ clientName: report.client1?.name, reportType: 'premium_html' });
      toast({ title: 'PDF downloaded', description: 'Report saved successfully.' });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({ title: 'PDF generation failed', description: error.message || 'Please try again.', variant: 'destructive' });
    }
  };

  const handleBackToForm = () => {
    setCurrentStep(STEPS.length - 2); // Back to Assumptions so user can adjust and regenerate
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 2) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === STEPS.length - 2) {
      generateReport();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addChild = () => {
    setChildren([...children, { name: '', dob: '' }]);
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);
  };

  const removeChild = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const addDebt = () => {
    setDebts([...debts, { description: '', balance: '', monthlyPayment: '', interestRate: '' }]);
  };

  const updateDebt = (index, field, value) => {
    const updated = [...debts];
    updated[index] = { ...updated[index], [field]: value };
    setDebts(updated);
  };

  const removeDebt = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const addExpense = () => {
    setExpenses([...expenses, { category: '', amount: '' }]);
  };
  const updateExpense = (index, field, value) => {
    const next = [...expenses];
    next[index] = { ...next[index], [field]: value };
    setExpenses(next);
  };
  const removeExpense = (index) => {
    setExpenses(expenses.filter((_, i) => i !== index));
  };

  const addAsset = () => {
    setAssets([...assets, { description: '', amount: '', monthlySavings: '', growthRate: '', availableForEmergency: false }]);
  };

  const updateAsset = (index, field, value) => {
    const updated = [...assets];
    updated[index] = { ...updated[index], [field]: value };
    setAssets(updated);
  };

  const removeAsset = (index) => {
    setAssets(assets.filter((_, i) => i !== index));
  };

  const addRetirementAsset = () => {
    setRetirementAssets([...retirementAssets, { description: '', owner: '', currentAmount: '', monthlySavings: '', companyMatch: '', growthRate: '' }]);
  };

  const updateRetirementAsset = (index, field, value) => {
    const updated = [...retirementAssets];
    updated[index] = { ...updated[index], [field]: value };
    setRetirementAssets(updated);
  };

  const removeRetirementAsset = (index) => {
    setRetirementAssets(retirementAssets.filter((_, i) => i !== index));
  };

  const addLifeInsurance = () => {
    setLifeInsurance([...lifeInsurance, { insured: '', company: '', deathBenefit: '', annualPremium: '' }]);
  };

  const updateLifeInsurance = (index, field, value) => {
    const updated = [...lifeInsurance];
    updated[index] = { ...updated[index], [field]: value };
    setLifeInsurance(updated);
  };

  const removeLifeInsurance = (index) => {
    setLifeInsurance(lifeInsurance.filter((_, i) => i !== index));
  };

  const addDisabilityInsurance = () => {
    setDisabilityInsurance([...disabilityInsurance, { insured: '', monthlyBenefit: '', monthlyPremium: '' }]);
  };

  const updateDisabilityInsurance = (index, field, value) => {
    const updated = [...disabilityInsurance];
    updated[index] = { ...updated[index], [field]: value };
    setDisabilityInsurance(updated);
  };

  const removeDisabilityInsurance = (index) => {
    setDisabilityInsurance(disabilityInsurance.filter((_, i) => i !== index));
  };

  const addLTCInsurance = () => {
    setLtcInsurance([...ltcInsurance, { insured: '', monthlyBenefit: '', monthlyPremium: '' }]);
  };

  const updateLTCInsurance = (index, field, value) => {
    const updated = [...ltcInsurance];
    updated[index] = { ...updated[index], [field]: value };
    setLtcInsurance(updated);
  };

  const removeLTCInsurance = (index) => {
    setLtcInsurance(ltcInsurance.filter((_, i) => i !== index));
  };

  const toggleAssumptionSection = (key) => {
    setAssumptionCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleGoal = (category, goal) => {
    const setter = category === 'short' ? setShortTermGoals : category === 'mid' ? setMidTermGoals : setLongTermGoals;
    const current = category === 'short' ? shortTermGoals : category === 'mid' ? midTermGoals : longTermGoals;
    if (current.includes(goal)) {
      setter(current.filter(g => g !== goal));
    } else {
      setter([...current, goal]);
    }
  };

  const commonGoals = [
    'Make a major purchase (car, furniture, boat, family vacation)',
    'Build savings for unexpected expenses (emergency fund)',
    'Alternate income in case of death or disability',
    'Build retirement wealth',
    'Reduce or pay off mortgage',
    'Help support aging parents',
    'Buy a new home',
    'College for child(ren)',
    'Pay off credit cards',
    'Start a business',
  ];

  return (
    <div className="fna-app">
      <div className="fna-header">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1>Financial Need Analysis</h1>
            <p>Build a personalized report with your client. Completing each step together increases commitment and makes findings more actionable.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="shadow-sm" onClick={() => navigate('/agents/financial-need-analysis')}>
              <Users className="w-4 h-4 mr-2" />
              View FNA Clients
            </Button>
            {agentProfile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground px-2">
                {autoSaveStatus === 'saving' && (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <Save className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">Saved</span>
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <>
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-red-600">Save failed</span>
                  </>
                )}
                {autoSaveStatus === 'idle' && (
                  <>
                    <Save className="w-4 h-4 opacity-50" />
                    <span className="opacity-50">Auto-save enabled</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 max-w-5xl mx-auto pb-8">
      {/* Saved Reports/Drafts Selector */}
      {agentProfile && (savedReports.length > 0 || savedDrafts.length > 0) && (
        <Card className="fna-card mb-6">
          <CardContent className="fna-card-content pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <FolderOpen className="w-5 h-5 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">Load Saved Client Data</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select a saved draft (complete form) or report (client info only) to continue working
                  </p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Drafts Selector */}
                {savedDrafts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Saved Drafts (All Fields)</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={selectedDraftId}
                      onChange={(e) => {
                        const draftId = e.target.value;
                        setSelectedDraftId(draftId);
                        if (draftId) {
                          const selectedDraft = savedDrafts.find(d => d.id === draftId);
                          const dataToLoad = selectedDraft?.formData ?? selectedDraft;
                          if (dataToLoad && typeof dataToLoad === 'object') {
                            setCurrentClientDocId(draftId);
                            loadDraftData(dataToLoad);
                          } else {
                            toast({ title: 'Unable to load draft', description: 'Draft data is missing or invalid.', variant: 'destructive' });
                            setSelectedDraftId('');
                          }
                        }
                      }}
                      disabled={loadingReports}
                    >
                      <option value="">Select a saved draft...</option>
                      {savedDrafts.map((draft) => {
                        const clientName = draft.formData?.client1?.name || draft.formData?.client2?.name || draft.clientEmail || 'Untitled';
                        let dateStr = 'Unknown date';
                        try {
                          if (draft.updatedAt?.toDate) {
                            dateStr = draft.updatedAt.toDate().toLocaleDateString();
                          } else if (draft.updatedAt) {
                            dateStr = new Date(draft.updatedAt.seconds * 1000).toLocaleDateString();
                          }
                        } catch (e) {
                          // Keep default
                        }
                        return (
                          <option key={draft.id} value={draft.id}>
                            {clientName} - {dateStr} (Draft)
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Reports Selector */}
                {savedReports.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Saved Reports (Client Info Only)</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={selectedReportId}
                      onChange={(e) => {
                        const reportId = e.target.value;
                        setSelectedReportId(reportId);
                        if (reportId) {
                          const selectedReport = savedReports.find(r => r.id === reportId);
                          if (selectedReport?.reportData) {
                            setCurrentClientDocId(reportId);
                            handleSelectReport(selectedReport);
                          } else {
                            toast({ title: 'Unable to load report', description: 'Report data is missing.', variant: 'destructive' });
                            setSelectedReportId('');
                          }
                        }
                      }}
                      disabled={loadingReports}
                    >
                      <option value="">Select a saved report...</option>
                      {savedReports.map((savedReport) => {
                        const clientName = savedReport.reportData?.client1?.name || 
                                         savedReport.reportData?.client2?.name || 
                                         'Unknown Client';
                        let dateStr = 'Unknown date';
                        try {
                          if (savedReport.createdAt?.toDate) {
                            dateStr = savedReport.createdAt.toDate().toLocaleDateString();
                          } else if (savedReport.createdAt) {
                            dateStr = new Date(savedReport.createdAt.seconds * 1000).toLocaleDateString();
                          } else if (typeof savedReport.createdAt === 'string') {
                            dateStr = new Date(savedReport.createdAt).toLocaleDateString();
                          }
                        } catch (e) {
                          // Keep default
                        }
                        return (
                          <option key={savedReport.id} value={savedReport.id}>
                            {clientName} - {dateStr} (Report)
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </div>
              
              {loadingReports && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Loading saved data...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Indicator */}
      <div className="fna-step-indicator">
        <span className="text-sm text-muted-foreground mr-2 shrink-0">Step {currentStep + 1} of {STEPS.length}</span>
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`fna-step-pill ${
                idx === currentStep ? 'active' : idx < currentStep ? 'done' : 'pending'
              }`}
            >
              {idx < currentStep && <CheckCircle2 className="w-4 h-4" />}
              <span>{step.label}</span>
            </div>
            {idx < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-muted-foreground mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 1: Personal Information */}
      {currentStep === 0 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h3 className="font-semibold">Client 1</h3>
                  <div className="grid gap-2">
                    <Label>Name</Label>
                    <Input value={client1.name} onChange={(e) => setClient1({ ...client1, name: e.target.value })} placeholder="Full name" />
                  </div>
                  <div className="grid gap-2">
                    <Label>Date of Birth</Label>
                    <Input type="date" value={client1.dob} onChange={(e) => setClient1({ ...client1, dob: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Gender</Label>
                    <select
                      className="border rounded-md px-3 py-2"
                      value={client1.gender}
                      onChange={(e) => setClient1({ ...client1, gender: e.target.value })}
                    >
                      <option>Male</option>
                      <option>Female</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold">Client 2 (Spouse - Optional)</h3>
                  <div className="grid gap-2">
                    <Label>Name</Label>
                    <Input value={client2.name} onChange={(e) => setClient2({ ...client2, name: e.target.value })} placeholder="Full name" />
                  </div>
                  <div className="grid gap-2">
                    <Label>Date of Birth</Label>
                    <Input type="date" value={client2.dob} onChange={(e) => setClient2({ ...client2, dob: e.target.value })} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Gender</Label>
                    <select
                      className="border rounded-md px-3 py-2"
                      value={client2.gender}
                      onChange={(e) => setClient2({ ...client2, gender: e.target.value })}
                    >
                      <option>Male</option>
                      <option>Female</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Address</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address, City, State ZIP" />
              </div>
              <div className="grid gap-2">
                <Label>Client Email <span className="text-destructive">*</span></Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" required />
                <p className="text-xs text-muted-foreground">Required for saving — used as unique client identifier in the database</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Children and Dependents</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addChild}>Add Child</Button>
                </div>
                {children.map((child, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Child name"
                      value={child.name}
                      onChange={(e) => updateChild(idx, 'name', e.target.value)}
                    />
                    <Input
                      type="date"
                      placeholder="Date of birth"
                      value={child.dob}
                      onChange={(e) => updateChild(idx, 'dob', e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeChild(idx)}>Remove</Button>
                  </div>
                ))}
              </div>
            </CardContent>
        </Card>
      )}

      {/* Step 2: Income & Assets */}
      {currentStep === 1 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Income & Assets</CardTitle>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{client1.name || 'Client 1'} Annual Salary</Label>
                  <Input
                    type="number"
                    value={client1Salary}
                    onChange={(e) => setClient1Salary(e.target.value)}
                    placeholder="e.g. 120000"
                  />
                </div>
                {client2.name && (
                  <div className="grid gap-2">
                    <Label>{client2.name} Annual Salary</Label>
                    <Input
                      type="number"
                      value={client2Salary}
                      onChange={(e) => setClient2Salary(e.target.value)}
                      placeholder="e.g. 130000"
                    />
                  </div>
                )}
              </div>
              <div className="grid gap-2">
                <Label>Estimated Average Income Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  placeholder="e.g. 20"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Assets (Savings, Investments, etc.)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addAsset}>Add Asset</Button>
                </div>
                {assets.map((asset, idx) => (
                  <div key={idx} className="grid md:grid-cols-6 gap-2 mb-2 p-3 border rounded-md">
                    <Input
                      placeholder="Description"
                      value={asset.description}
                      onChange={(e) => updateAsset(idx, 'description', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Current Amount"
                      value={asset.amount}
                      onChange={(e) => updateAsset(idx, 'amount', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Savings"
                      value={asset.monthlySavings}
                      onChange={(e) => updateAsset(idx, 'monthlySavings', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Growth Rate %"
                      value={asset.growthRate}
                      onChange={(e) => updateAsset(idx, 'growthRate', e.target.value)}
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={asset.availableForEmergency || false}
                        onChange={(e) => updateAsset(idx, 'availableForEmergency', e.target.checked)}
                      />
                      <span className="text-sm">Emergency Fund?</span>
                    </label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAsset(idx)}>Remove</Button>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Retirement Assets (401K, IRA, etc.)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addRetirementAsset}>Add Retirement Asset</Button>
                </div>
                {retirementAssets.map((asset, idx) => (
                  <div key={idx} className="grid md:grid-cols-7 gap-2 mb-2 p-3 border rounded-md">
                    <Input
                      placeholder="Description"
                      value={asset.description}
                      onChange={(e) => updateRetirementAsset(idx, 'description', e.target.value)}
                    />
                    <Input
                      placeholder="Owner"
                      value={asset.owner}
                      onChange={(e) => updateRetirementAsset(idx, 'owner', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Current Amount"
                      value={asset.currentAmount}
                      onChange={(e) => updateRetirementAsset(idx, 'currentAmount', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Savings"
                      value={asset.monthlySavings}
                      onChange={(e) => updateRetirementAsset(idx, 'monthlySavings', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Company Match"
                      value={asset.companyMatch}
                      onChange={(e) => updateRetirementAsset(idx, 'companyMatch', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Growth Rate %"
                      value={asset.growthRate}
                      onChange={(e) => updateRetirementAsset(idx, 'growthRate', e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeRetirementAsset(idx)}>Remove</Button>
                  </div>
                ))}
              </div>

              <div className="grid gap-2">
                <Label>Current Education Savings</Label>
                <Input
                  type="number"
                  value={educationSavings}
                  onChange={(e) => setEducationSavings(e.target.value)}
                  placeholder="e.g. 0"
                />
              </div>
            </CardContent>
        </Card>
      )}

      {/* Step 3: Debts */}
      {currentStep === 2 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Debts</CardTitle>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-4">
              <div className="flex justify-between items-center mb-2">
                <Label>Debts (Mortgage, Car Loans, Credit Cards, etc.)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addDebt}>Add Debt</Button>
              </div>
              {debts.map((debt, idx) => (
                <div key={idx} className="grid md:grid-cols-5 gap-2 p-3 border rounded-md">
                  <Input
                    placeholder="Description (e.g. House, Car)"
                    value={debt.description}
                    onChange={(e) => updateDebt(idx, 'description', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Balance"
                    value={debt.balance}
                    onChange={(e) => updateDebt(idx, 'balance', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Monthly Payment"
                    value={debt.monthlyPayment}
                    onChange={(e) => updateDebt(idx, 'monthlyPayment', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Interest Rate %"
                    value={debt.interestRate}
                    onChange={(e) => updateDebt(idx, 'interestRate', e.target.value)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeDebt(idx)}>Remove</Button>
                </div>
              ))}
            </CardContent>
        </Card>
      )}

      {/* Step 4: Expenses */}
      {currentStep === 3 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Monthly Expenses</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">This drives your Cash Flow and shows how much room you have to act on the rest of the plan. Enter estimated monthly amounts.</p>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-4">
            <div className="flex justify-between items-center mb-2">
              <Label>Expense Categories (monthly)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addExpense}>Add Expense</Button>
            </div>
            {expenses.map((row, idx) => (
              <div key={idx} className="grid md:grid-cols-12 gap-2 p-3 border rounded-lg items-center">
                <div className="md:col-span-7">
                  <Input
                    placeholder="e.g. Food and Clothing, Utilities"
                    value={row.category}
                    onChange={(e) => updateExpense(idx, 'category', e.target.value)}
                  />
                </div>
                <div className="md:col-span-4">
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={row.amount}
                    onChange={(e) => updateExpense(idx, 'amount', e.target.value)}
                  />
                </div>
                <div className="md:col-span-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeExpense(idx)}>Remove</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Existing Insurance */}
      {currentStep === 4 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Existing Insurance</CardTitle>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Life Insurance Policies</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLifeInsurance}>Add Policy</Button>
                </div>
                {lifeInsurance.map((policy, idx) => (
                  <div key={idx} className="grid md:grid-cols-5 gap-2 mb-2 p-3 border rounded-md">
                    <Input
                      placeholder="Insured Name"
                      value={policy.insured}
                      onChange={(e) => updateLifeInsurance(idx, 'insured', e.target.value)}
                    />
                    <Input
                      placeholder="Company"
                      value={policy.company}
                      onChange={(e) => updateLifeInsurance(idx, 'company', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Death Benefit"
                      value={policy.deathBenefit}
                      onChange={(e) => updateLifeInsurance(idx, 'deathBenefit', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Annual Premium"
                      value={policy.annualPremium}
                      onChange={(e) => updateLifeInsurance(idx, 'annualPremium', e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLifeInsurance(idx)}>Remove</Button>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Disability Income Insurance</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addDisabilityInsurance}>Add Policy</Button>
                </div>
                {disabilityInsurance.map((policy, idx) => (
                  <div key={idx} className="grid md:grid-cols-4 gap-2 mb-2 p-3 border rounded-md">
                    <Input
                      placeholder="Insured Name"
                      value={policy.insured}
                      onChange={(e) => updateDisabilityInsurance(idx, 'insured', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Benefit"
                      value={policy.monthlyBenefit}
                      onChange={(e) => updateDisabilityInsurance(idx, 'monthlyBenefit', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Premium"
                      value={policy.monthlyPremium}
                      onChange={(e) => updateDisabilityInsurance(idx, 'monthlyPremium', e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeDisabilityInsurance(idx)}>Remove</Button>
                  </div>
                ))}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Long-Term Care Insurance</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLTCInsurance}>Add Policy</Button>
                </div>
                {ltcInsurance.map((policy, idx) => (
                  <div key={idx} className="grid md:grid-cols-4 gap-2 mb-2 p-3 border rounded-md">
                    <Input
                      placeholder="Insured Name"
                      value={policy.insured}
                      onChange={(e) => updateLTCInsurance(idx, 'insured', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Benefit"
                      value={policy.monthlyBenefit}
                      onChange={(e) => updateLTCInsurance(idx, 'monthlyBenefit', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Monthly Premium"
                      value={policy.monthlyPremium}
                      onChange={(e) => updateLTCInsurance(idx, 'monthlyPremium', e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLTCInsurance(idx)}>Remove</Button>
                  </div>
                ))}
              </div>
            </CardContent>
        </Card>
      )}

      {/* Step 6: Goals */}
      {currentStep === 5 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Life Goals & Objectives</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">These goals will appear in your report and shape your recommendations—so your plan is built around what matters to you.</p>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-6">
              <div>
                <h3 className="font-semibold mb-3">Short-term Dreams (1 to 3 years)</h3>
                <div className="space-y-2">
                  {commonGoals.map((goal) => (
                    <label key={goal} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={shortTermGoals.includes(goal)}
                        onChange={() => toggleGoal('short', goal)}
                        className="rounded"
                      />
                      <span className="text-sm">{goal}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Mid-term Dreams (3 to 7 years)</h3>
                <div className="space-y-2">
                  {commonGoals.map((goal) => (
                    <label key={goal} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={midTermGoals.includes(goal)}
                        onChange={() => toggleGoal('mid', goal)}
                        className="rounded"
                      />
                      <span className="text-sm">{goal}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Long-term Dreams (7+ years)</h3>
                <div className="space-y-2">
                  {commonGoals.map((goal) => (
                    <label key={goal} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={longTermGoals.includes(goal)}
                        onChange={() => toggleGoal('long', goal)}
                        className="rounded"
                      />
                      <span className="text-sm">{goal}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Retirement Age</Label>
                  <Input
                    type="number"
                    value={retirementAge}
                    onChange={(e) => setRetirementAge(e.target.value)}
                    placeholder="e.g. 65"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Retirement Income Percent (% of current income)</Label>
                  <Input
                    type="number"
                    value={retirementIncomePercent}
                    onChange={(e) => setRetirementIncomePercent(e.target.value)}
                    placeholder="e.g. 80"
                  />
                </div>
              </div>
            </CardContent>
        </Card>
      )}

      {/* Step 7: Assumptions */}
      {currentStep === 6 && (
        <Card className="fna-form-card">
          <CardHeader className="fna-form-card-header border-0 bg-muted/50">
            <CardTitle className="fna-form-card-title">Report Assumptions</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Review and modify these values used to generate the FNA report. Defaults are provided; override as needed.</p>
          </CardHeader>
          <CardContent className="fna-form-card-content space-y-3">
              {(() => {
                const Section = ({ id, title, children }) => (
                  <div className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted text-left font-medium"
                      onClick={() => toggleAssumptionSection(id)}
                    >
                      {title}
                      {assumptionCollapsed[id] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                    {!assumptionCollapsed[id] && <div className="p-4 space-y-4">{children}</div>}
                  </div>
                );
                const Field = ({ label, value, onChange, placeholder, suffix = '' }) => (
                  <div className="grid gap-2">
                    <Label className="text-sm">{label}</Label>
                    <div className="flex items-center gap-2">
                      <Input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="max-w-[140px]" />
                      {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
                    </div>
                  </div>
                );
                return (
                  <>
                    <Section id="tax" title="Tax">
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Effective tax rate" value={assumptionTaxRate} onChange={setAssumptionTaxRate} placeholder="20" suffix="%" />
                      </div>
                    </Section>
                    <Section id="inflation" title="Inflation & Growth">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field label="General inflation" value={assumptionInflationRate} onChange={setAssumptionInflationRate} placeholder="3" suffix="%" />
                        <Field label="Education cost inflation" value={assumptionEducationInflation} onChange={setAssumptionEducationInflation} placeholder="6" suffix="%" />
                        <Field label="LTC inflation" value={assumptionLTCInflation} onChange={setAssumptionLTCInflation} placeholder="3" suffix="%" />
                      </div>
                    </Section>
                    <Section id="investment" title="Investment Return Rates">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field label="Survivor / income replacement" value={assumptionSurvivorReturn} onChange={setAssumptionSurvivorReturn} placeholder="5" suffix="%" />
                        <Field label="Retirement growth" value={assumptionRetirementGrowth} onChange={setAssumptionRetirementGrowth} placeholder="8" suffix="%" />
                        <Field label="Education savings return" value={assumptionReturnRate} onChange={setAssumptionReturnRate} placeholder="5" suffix="%" />
                        <Field label="LTC savings return" value={assumptionLTCReturn} onChange={setAssumptionLTCReturn} placeholder="4" suffix="%" />
                      </div>
                    </Section>
                    <Section id="survivor" title="Survivor / Life Insurance">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Field label="Income replacement % (with kids)" value={assumptionSurvivorIncomePctWithKids} onChange={setAssumptionSurvivorIncomePctWithKids} placeholder="70" suffix="%" />
                        <Field label="Income replacement % (after kids)" value={assumptionSurvivorIncomePctAfterKids} onChange={setAssumptionSurvivorIncomePctAfterKids} placeholder="60" suffix="%" />
                        <Field label="Final expenses" value={assumptionFinalExpenses} onChange={setAssumptionFinalExpenses} placeholder="10000" suffix="$" />
                        <Field label="Life expectancy" value={assumptionLifeExpectancy} onChange={setAssumptionLifeExpectancy} placeholder="81" suffix="years" />
                      </div>
                    </Section>
                    <Section id="emergency" title="Emergency Fund">
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Months of expenses" value={assumptionEmergencyMonths} onChange={setAssumptionEmergencyMonths} placeholder="3" suffix="months" />
                      </div>
                    </Section>
                    <Section id="education" title="Education">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Field label="Annual cost today (per child)" value={assumptionEducationCost} onChange={setAssumptionEducationCost} placeholder="50000" suffix="$" />
                        <Field label="Years of college" value={assumptionEducationYears} onChange={setAssumptionEducationYears} placeholder="4" suffix="years" />
                        <Field label="Percent to fund" value={assumptionEducationPercentFund} onChange={setAssumptionEducationPercentFund} placeholder="100" suffix="%" />
                      </div>
                    </Section>
                    <Section id="retirement" title="Retirement">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Field label="Years in retirement" value={assumptionRetirementYears} onChange={setAssumptionRetirementYears} placeholder="20" suffix="years" />
                        <Field label="Social Security (annual)" value={assumptionSocialSecurity} onChange={setAssumptionSocialSecurity} placeholder="0" suffix="$" />
                      </div>
                    </Section>
                    <Section id="disability" title="Disability">
                      <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Income replacement %" value={assumptionDisabilityPct} onChange={setAssumptionDisabilityPct} placeholder="60" suffix="%" />
                      </div>
                    </Section>
                    <Section id="ltc" title="Long-Term Care">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Field label="Age LTC need starts" value={assumptionLTCAge} onChange={setAssumptionLTCAge} placeholder="75" suffix="years" />
                        <Field label="Monthly LTC cost today" value={assumptionLTCCost} onChange={setAssumptionLTCCost} placeholder="6000" suffix="$" />
                        <Field label="Duration" value={assumptionLTCDuration} onChange={setAssumptionLTCDuration} placeholder="3" suffix="years" />
                      </div>
                    </Section>
                  </>
                );
              })()}
          </CardContent>
        </Card>
      )}

      {/* Step 8: Report */}
      {currentStep === 7 && report && (
        <FNAReportView 
          report={report} 
          onBack={handleBackToForm}
          onDownload={handleDownloadPdf}
        />
      )}

      {/* Legacy Report View - Hidden, keeping for reference */}
      {false && currentStep === 5 && report && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Financial Need Analysis Report</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleShareByEmail}>
                    <Mail className="w-4 h-4 mr-2" />
                    Share by Email
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPdf}>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-0">
              {/* Cash Flow Summary */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Cash Flow Summary</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p><strong>Gross Monthly Income:</strong> ${report.calculations.cashFlow.grossIncome.toLocaleString()}</p>
                    <p><strong>Monthly Taxes:</strong> ${report.calculations.cashFlow.taxes.toLocaleString()}</p>
                    <p><strong>Net Income:</strong> ${report.calculations.cashFlow.netIncome.toLocaleString()}</p>
                  </div>
                  <div>
                    <p><strong>Total Monthly Expenses:</strong> ${report.calculations.cashFlow.totalExpenses.toLocaleString()}</p>
                    <p><strong>Discretionary Income:</strong> ${report.calculations.cashFlow.discretionaryIncome.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Survivor Needs */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Survivor Needs (Life Insurance)</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{report.client1.name || 'Client 1'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-2"><strong>Total Need:</strong> ${report.calculations.survivor1.totalNeed.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Existing Insurance:</strong> ${report.calculations.survivor1.existingInsurance.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Remaining Need:</strong> ${report.calculations.survivor1.remainingNeed.toLocaleString()}</p>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progress</span>
                          <span>{report.calculations.survivor1.progress.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${report.calculations.survivor1.progress}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  {report.client2.name && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">{report.client2.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-2"><strong>Total Need:</strong> ${report.calculations.survivor2.totalNeed.toLocaleString()}</p>
                        <p className="text-sm mb-2"><strong>Existing Insurance:</strong> ${report.calculations.survivor2.existingInsurance.toLocaleString()}</p>
                        <p className="text-sm mb-2"><strong>Remaining Need:</strong> ${report.calculations.survivor2.remainingNeed.toLocaleString()}</p>
                        <div className="mt-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Progress</span>
                            <span>{report.calculations.survivor2.progress.toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${report.calculations.survivor2.progress}%` }}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Disability Needs */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Disability Income Needs</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{report.client1.name || 'Client 1'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-2"><strong>Monthly Need:</strong> ${report.calculations.disability1.monthlyNeed.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Remaining Monthly Need:</strong> ${report.calculations.disability1.monthlyRemaining.toLocaleString()}</p>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progress</span>
                          <span>{report.calculations.disability1.progress.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${report.calculations.disability1.progress}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  {report.client2.name && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">{report.client2.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-2"><strong>Monthly Need:</strong> ${report.calculations.disability2.monthlyNeed.toLocaleString()}</p>
                        <p className="text-sm mb-2"><strong>Remaining Monthly Need:</strong> ${report.calculations.disability2.monthlyRemaining.toLocaleString()}</p>
                        <div className="mt-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span>Progress</span>
                            <span>{report.calculations.disability2.progress.toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${report.calculations.disability2.progress}%` }}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Long-Term Care */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Long-Term Care Needs</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">{report.client1.name || 'Client 1'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-2"><strong>Monthly Need at Age 75:</strong> ${report.calculations.ltc1.monthlyNeedAtLTC.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Remaining Monthly Need:</strong> ${report.calculations.ltc1.remainingMonthlyNeed.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Monthly Savings Needed:</strong> ${report.calculations.ltc1.monthlySavingsNeeded.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  {report.client2.name && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">{report.client2.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-2"><strong>Monthly Need at Age 75:</strong> ${report.calculations.ltc2.monthlyNeedAtLTC.toLocaleString()}</p>
                        <p className="text-sm mb-2"><strong>Remaining Monthly Need:</strong> ${report.calculations.ltc2.remainingMonthlyNeed.toLocaleString()}</p>
                        <p className="text-sm mb-2"><strong>Monthly Savings Needed:</strong> ${report.calculations.ltc2.monthlySavingsNeeded.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Emergency Fund */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Emergency Fund</h3>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm mb-2"><strong>Total Need:</strong> ${report.calculations.emergencyFund.totalNeed.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Current Fund:</strong> ${report.calculations.emergencyFund.currentEmergencyFund.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Remaining Need:</strong> ${report.calculations.emergencyFund.remainingNeed.toLocaleString()}</p>
                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{report.calculations.emergencyFund.progress.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${report.calculations.emergencyFund.progress}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Debt Management */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Debt Management</h3>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm mb-2"><strong>Total Debt:</strong> ${report.calculations.debtManagement.totalDebt.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Debt-to-Income Ratio:</strong> {report.calculations.debtManagement.debtToIncomeRatio.toFixed(2)}%</p>
                    <p className={`text-sm ${report.calculations.debtManagement.isHealthy ? 'text-green-600' : 'text-orange-600'}`}>
                      {report.calculations.debtManagement.isHealthy ? '✓ Healthy ratio (< 20%)' : '⚠ Consider reducing debt'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Retirement */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Retirement Goal</h3>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm mb-2"><strong>Lump Sum Needed:</strong> ${report.calculations.retirement.lumpSumNeeded.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Projected Assets:</strong> ${report.calculations.retirement.projectedAssets.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Shortfall:</strong> ${report.calculations.retirement.shortfall.toLocaleString()}</p>
                    <p className="text-sm mb-2"><strong>Monthly Savings Needed:</strong> ${report.calculations.retirement.monthlySavingsNeeded.toLocaleString()}</p>
                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{report.calculations.retirement.progress.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${report.calculations.retirement.progress}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Education */}
              {children.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Education Goal</h3>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm mb-2"><strong>Total Lump Sum Needed:</strong> ${report.calculations.education.totalLumpSumNeeded.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Current Savings:</strong> ${report.calculations.education.currentEducationSavings.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Shortfall:</strong> ${report.calculations.education.shortfall.toLocaleString()}</p>
                      <p className="text-sm mb-2"><strong>Monthly Savings Needed:</strong> ${report.calculations.education.monthlySavingsNeeded.toLocaleString()}</p>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progress</span>
                          <span>{report.calculations.education.progress.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{ width: `${report.calculations.education.progress}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-6 space-y-3">
        {currentStep === STEPS.length - 2 && (
          <p className="text-sm text-muted-foreground text-center sm:text-left">Your report will show where you stand today, where gaps are, and clear next steps. Review it together with your client to build commitment.</p>
        )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={prevStep} disabled={currentStep === 0}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        {currentStep < STEPS.length - 1 && (
          <Button onClick={nextStep}>
            {currentStep === STEPS.length - 2 ? 'Generate Report' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
